import { useCallback, useEffect, useRef } from 'react';
import { EVO_STICKY_TOP_PX, pageSectionIds } from '@/app/components/hotel-detail/common/consts';

// Pause before the page starts moving (the ticket asks for 0.5–1s): long enough for the click to land and
// the loading panel to appear, short enough to still read as a reaction to the click.
const SCROLL_TO_ROOMS_DELAY_MS = 700;
const SCROLL_TO_ROOMS_DURATION_MS = 600;

// The user taking over the page, by any input: pending or under way, the scroll must yield at once. Not
// `scroll` itself, which the animation fires too.
const USER_INTERACTION_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

type ScheduledScrollOptions = {
  delayMs: number;
  durationMs: number;
  /** Distance kept between the viewport top and the target's top edge. */
  offsetPx?: number;
};

/**
 * Smoothly scrolls the window to `getTarget()` after a delay. Cancelled by any user interaction from the
 * moment it is scheduled until the animation ends, so it never fights the user for the scroll position.
 * Returns a function that cancels it.
 *
 * Own rAF animation rather than `scrollTo({ behavior: 'smooth' })`: a native smooth scroll cannot be
 * stopped by a key press or a tap once started, and re-reading the target's position every frame lets
 * the destination follow content above it that is still settling (on a phone the flight summary above
 * the rooms grows when the transport lands).
 */
export function scheduleSmoothScrollTo(
  getTarget: () => HTMLElement | null,
  { delayMs, durationMs, offsetPx = 0 }: ScheduledScrollOptions
): () => void {
  let listenTimer: ReturnType<typeof setTimeout> | undefined;
  let startTimer: ReturnType<typeof setTimeout> | undefined;
  let frame = 0;
  let listening = false;

  const cancel = () => {
    clearTimeout(listenTimer);
    clearTimeout(startTimer);
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    if (listening) {
      USER_INTERACTION_EVENTS.forEach((type) => window.removeEventListener(type, cancel, true));
      listening = false;
    }
  };

  // Listeners go on a tick later: scheduled from a click or keydown handler, the very event being handled
  // (Enter on a calendar day, say) is still propagating and would cancel the scroll it just requested.
  listenTimer = setTimeout(() => {
    USER_INTERACTION_EVENTS.forEach((type) => window.addEventListener(type, cancel, { capture: true, passive: true }));
    listening = true;
  }, 0);

  startTimer = setTimeout(() => {
    const target = getTarget();
    if (!target) {
      cancel();
      return;
    }
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const duration = reduceMotion ? 0 : durationMs;
    const startY = window.scrollY;
    const startTime = performance.now();
    const destination = () => {
      const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      return Math.min(Math.max(0, target.getBoundingClientRect().top + window.scrollY - offsetPx), maxY);
    };
    const step = (now: number) => {
      const progress = duration > 0 ? Math.min(1, (now - startTime) / duration) : 1;
      window.scrollTo(0, startY + (destination() - startY) * easeInOutCubic(progress));
      if (progress < 1) frame = requestAnimationFrame(step);
      else cancel();
    };
    step(startTime);
  }, delayMs);

  return cancel;
}

/**
 * Scrolls the page to the rooms section, its title `EVO_STICKY_TOP_PX` below the viewport top so it lines
 * up with the sticky trip-overview card, a moment after the call: for a stay picked in the apex calendar,
 * so the user lands on the rooms being re-priced (MPH-971). Any interaction meanwhile cancels it; so does
 * a newer call, or unmounting. A no-op if the rooms section is not in the DOM.
 */
export function useScrollToRooms() {
  const cancelRef = useRef<() => void>(() => {});

  useEffect(() => () => cancelRef.current(), []);

  return useCallback(() => {
    cancelRef.current();
    cancelRef.current = scheduleSmoothScrollTo(() => document.getElementById(pageSectionIds.rooms), {
      delayMs: SCROLL_TO_ROOMS_DELAY_MS,
      durationMs: SCROLL_TO_ROOMS_DURATION_MS,
      offsetPx: EVO_STICKY_TOP_PX,
    });
  }, []);
}
